import { createContext, useContext } from "react";

import { SelectData } from "../../models";
import { GetTaskBoardResponse } from "../../service/query";
import { PanelTask } from "./taskPanelMode";
import { UseWorkflowTaskPanelReturnData } from "./useWorkflowTaskPanel";

/*
  Everything the overlay's parts read. The panel is opened from many hosts, so it cannot take its state from
  a host context (see useWorkflowTaskPanel) — but its own parts are only ever rendered inside it, so they
  can take theirs from here rather than through ~20 props each.
*/
export interface TaskPanelContextValue extends UseWorkflowTaskPanelReturnData {
  // The task being edited, or undefined while creating.
  task: PanelTask | undefined;
  /*
    The workflow this task actually belongs to. The panel drops one that is still the previously selected
    task's, so this is not simply the workflow prop the host passed.
  */
  workflow: GetTaskBoardResponse["results"][number] | undefined;
  // Parent name shown under the title: the task's own on edit, the host's on create.
  parentName: string | undefined;
  // Phase controls, supplied by hosts that model phases differently (the deal board labels them Stages).
  phaseOptions?: SelectData;
  phaseLabel?: string;
  isRepeatablePhase?: (phase: string | null | undefined) => boolean;
  handleRefresh: () => void;
}

const TaskPanelContext = createContext<TaskPanelContextValue | undefined>(undefined);

export const TaskPanelProvider = TaskPanelContext.Provider;

export const useTaskPanelContext = (): TaskPanelContextValue => {
  const value = useContext(TaskPanelContext);

  // Undefined only if a part is rendered outside the panel, which is a wiring mistake rather than a state
  // the parts should each guard against.
  if (value === undefined) {
    throw new Error("useTaskPanelContext must be used within a WorkflowTaskPanel");
  }

  return value;
};
